// frontend/src/hooks/useCurrentPosition.js
/**
 * Minimal geolocation helper for "Use my location" flows.
 *
 * Returns the last successful position, any error message, a loading flag,
 * and requestPosition() to (re)prompt the browser. Does not watch — callers
 * that need continuous tracking should use navigator.geolocation directly.
 */

import { useCallback, useState } from 'react';

const DEFAULT_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 60000,
};

/**
 * @returns {{
 *   position: GeolocationPosition|null,
 *   error: string|null,
 *   loading: boolean,
 *   requestPosition: (options?: PositionOptions) => void,
 * }}
 */
const useCurrentPosition = () => {
  const [position, setPosition] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const requestPosition = useCallback((options = {}) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setError('Geolocation is not supported on this device.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition(pos);
        setError(null);
        setLoading(false);
      },
      (err) => {
        const message = err?.code === 1
          ? 'Location permission denied.'
          : err?.code === 2
            ? 'Location unavailable.'
            : err?.code === 3
              ? 'Location request timed out.'
              : 'Could not get your location.';
        setError(message);
        setLoading(false);
      },
      { ...DEFAULT_OPTIONS, ...options },
    );
  }, []);

  return { position, error, loading, requestPosition };
};

export default useCurrentPosition;
